import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  MenuItem,
  Box,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { SimCard } from '../pages/sims/SimManagement';

type UsageLimitOverrideModalProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  sim: SimCard;
};

function UsageLimitOverrideModal({ open, onClose, sim }: UsageLimitOverrideModalProps) {
  // Possible override values: null (inherit), specific number (override)
  const [overrideValue, setOverrideValue] = useState<string | number | null>(
    sim.usage_limit_override_value === null ? 'inherit' : sim.usage_limit_override_value
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inheritedValue = sim.clusters?.usage_limit_value ?? null;

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert to appropriate type for database
      let valueToSave: number | null;
      
      if (overrideValue === 'inherit') {
        valueToSave = null; // Inherit from cluster
      } else if (overrideValue === '' || overrideValue === null) {
        valueToSave = null; // Inherit from cluster
      } else {
        valueToSave = Number(overrideValue);
        
        // Validate the number is non-negative
        if (isNaN(valueToSave) || valueToSave < 0) {
          throw new Error('Usage limit must be a non-negative number');
        }
      }

      const { error: updateError } = await supabase
        .from('sim_cards')
        .update({ usage_limit_override_value: valueToSave })
        .eq('id', sim.id);

      if (updateError) throw updateError;
      onClose(true); // Close and trigger refresh
    } catch (err: any) {
      console.error("Error saving usage limit override:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (value === 'inherit') {
      setOverrideValue('inherit');
    } else if (value === '') {
      setOverrideValue('');
    } else {
      // Try to convert to number for all other inputs
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        setOverrideValue(numValue);
      }
    }
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) return 'No limit';
    if (limit === 0) return 'R0 (blocks all auto top-ups)';
    return `R${limit.toFixed(2)}`;
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Set Monthly Usage Limit for {sim.msisdn || sim.iccid}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="body1" gutterBottom>
            Set a monthly spending limit for automatic top-ups for this SIM.
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Inherited Limit from Cluster '{sim.clusters?.name || 'N/A'}': <strong>{formatLimit(inheritedValue)}</strong>
          </Typography>
        </Box>
        
        <TextField
          select
          fullWidth
          label="Usage Limit Setting"
          value={overrideValue === null ? 'inherit' : overrideValue === '' ? 'inherit' : overrideValue}
          onChange={handleChange}
          sx={{ mb: 2 }}
        >
          <MenuItem value="inherit">Inherit from Cluster ({formatLimit(inheritedValue)})</MenuItem>
          <MenuItem value={0}>R0 (Block all auto top-ups)</MenuItem>
          <MenuItem value={50}>R50</MenuItem>
          <MenuItem value={100}>R100</MenuItem>
          <MenuItem value={200}>R200</MenuItem>
          <MenuItem value={500}>R500</MenuItem>
          <MenuItem value={1000}>R1000</MenuItem>
        </TextField>
        
        <TextField
          type="number"
          label="Custom Limit (ZAR)"
          value={typeof overrideValue === 'number' && ![0, 50, 100, 200, 500, 1000].includes(overrideValue) ? overrideValue : ''}
          onChange={handleChange}
          fullWidth
          InputProps={{
            inputProps: { min: 0, step: 10 }
          }}
          helperText="Set a custom ZAR limit or choose a preset value above"
        />
        
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UsageLimitOverrideModal; 